import * as React from 'react';
import {Page} from "../app/Page";
import {useCallback, useEffect, useRef} from "react";
import {httpClient} from "../utils/httpClient";
import AppConsts from "../app/AppConsts";
import {logError} from "../utils/logging";
import {Box, CircularProgress} from "@mui/material";
import {PathsMap} from "../app/PageRouting";
import {useUser} from "./UserProvider";
import {useHistory} from "react-router";
import {useSnackbar} from "../providers/SnackbarProvider";
import {t} from "i18next";
import Keycloak from "keycloak-js";

export function useSSOPublicClient() {
    const keycloak = new Keycloak({
        url: process.env.REACT_APP_KEYCLOAK_URL,
        realm: process.env.REACT_APP_KEYCLOAK_REALM,
        clientId: process.env.REACT_APP_OAUTH_CLIENT_ID,
        scope: "openid profile group microprofile-jwt roles email address web-origins offline_access phone basic application acr"
    });

    const fns = useRef({
        login: () => {
            return keycloak.init({
                onLoad: "login-required",
                checkLoginIframe: false,
                pkceMethod: "S256",
            });
        },
        runSilentCheck: () => {
            return new Promise((success, fail) => {
                let iframe;

                function handler(event) {
                    if (event.origin !== window.location.origin || !event.data?.oAuthSilentCheck) {
                        return;
                    }

                    window.removeEventListener("message", handler);
                    iframe.remove();

                    if (event.data.oAuthSilentCheck.success) {
                        keycloak.token = event.data.oAuthSilentCheck.data.token;
                        success({
                            token: event.data.oAuthSilentCheck.data.token,
                            tokenParsed: event.data.oAuthSilentCheck.data.tokenParsed
                        });
                    } else {
                        fail();
                    }
                }

                iframe = document.createElement("iframe");
                iframe.src = window.location.origin + PathsMap.OAUTH_SILENT_CHECK;
                iframe.title = "OAuth check";
                iframe.setAttribute("aria-hidden", "true");
                iframe.style.display = "none";

                window.addEventListener("message", handler);
                document.body.appendChild(iframe);
            });
        },
        run: () => {
            return new Promise((success, fail) => {
                keycloak.init({
                    onLoad: "check-sso",
                    checkLoginIframe: false,
                    pkceMethod: "S256",
                }).then(authenticated => {
                    if (!authenticated) {
                        fail('NOT_AUTHENTICATED');
                        return;
                    }

                    // Rinnova il token se scade entro 30 secondi; se non è vicino alla scadenza
                    // updateToken risolve subito senza fare alcuna chiamata di rete.
                    keycloak.updateToken(30).then(() => {
                        success({
                            token: keycloak.token,
                            tokenParsed: keycloak.tokenParsed
                        });
                    }).catch(() => {
                        fail('TOKEN_NOT_REFRESHED');
                    });
                });
            });
        },
        getToken: () => keycloak.token
    });

    return fns.current;
}

export function OAuthSilentCheck() {
    const sso = useSSOPublicClient();

    useEffect(() => {
        const postSilentCheckResult = (message) => {
            const targetWindow = window.parent !== window ? window.parent : window.opener;

            if (targetWindow) {
                targetWindow.postMessage(message, window.location.origin);
            }

            if (window.parent === window && window.opener) {
                window.close();
            }
        };

        sso.run().then(data => {
            postSilentCheckResult({oAuthSilentCheck: {success: true, data: data}});
        }).catch(e => {
            logError(e);
            postSilentCheckResult({oAuthSilentCheck: {success: false}});
        });
    }, [sso]);

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
        >
            <CircularProgress/>
        </Box>
    );
}

export function OAuthPublicClient() {
    const syncExecuted = useRef(false);
    const user = useUser();
    const history = useHistory();
    const {createSnackbar} = useSnackbar();
    const sso = useSSOPublicClient();

    const handleError = useCallback(() => {
        createSnackbar({
            message: t('sign_in.oauth_error_occurred'),
            type: "error",
            autoHideDuration: 2000
        });
        history.push(PathsMap.SIGN_IN);
    }, [createSnackbar, history]);

    useEffect(() => {
        if (syncExecuted.current) {
            return;
        }
        syncExecuted.current = true;

        sso.login().then(authenticated => {
            if (authenticated) {
                httpClient.post(`${AppConsts.API_PATH}/users/auth/oauth-sync`, {
                    token: sso.getToken()
                }).then(response => {
                    if (response.data.accessToken) {
                        user.set({
                            tokens: {
                                accessToken: response.data.accessToken,
                                refreshToken: response.data.refreshToken,
                                expiresAt: response.data.expiresAt
                            }
                        }).then(() => {
                            setTimeout(() => history.push(PathsMap.HOME), 0);
                        });
                    } else {
                        handleError();
                    }
                }).catch((e) => {
                    logError(e);
                    handleError();
                });
            } else {
                handleError();
            }
        }).catch(e => {
            logError(e);
            handleError();
        });
    }, [handleError, history, sso, user]);

    return (
        <Page centered>
            <Box justifyContent="center" display="flex">
                <CircularProgress/>
            </Box>
        </Page>
    );
}

export function OAuthConfidentialCallback() {
    const user = useUser();
    const history = useHistory();
    const callbackExecuted = useRef(false);
    const {createSnackbar} = useSnackbar();

    const handleError = useCallback(() => {
        createSnackbar({
            message: t('sign_in.oauth_error_occurred'),
            type: "error",
            autoHideDuration: 2000
        });
        history.push(PathsMap.SIGN_IN);
    }, [createSnackbar, history]);

    useEffect(() => {
        if (callbackExecuted.current) {
            return;
        }
        callbackExecuted.current = true;

        const urlParams = new URLSearchParams(window.location.search);
        httpClient.post(`${AppConsts.API_PATH}/users/auth/oauth-callback`, {
            code: urlParams.get('code')
        }).then(response => {
            if (response.data.accessToken) {
                user.set({
                    tokens: {
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                        expiresAt: response.data.expiresAt
                    }
                }).then(() => {
                    setTimeout(() => history.push(PathsMap.HOME), 0);
                });
            } else {
                handleError();
            }
        }).catch((e) => {
            logError(e);
            handleError();
        });
    }, [history, user, createSnackbar, handleError]);

    return (
        <Page centered>
            <Box justifyContent="center" display="flex">
                <CircularProgress/>
            </Box>
        </Page>
    );
}
